import * as Yup from 'yup';
import {
  addressNotRequiredSchema,
  addressSchema,
  emailSchema,
  foreignTaxNumberSchema,
  nameSchema,
  notRequiredOptionSchema,
  phoneSchema,
  requiredOptionSchema,
  RNOKPPIdentNumberSchema,
} from '../../validationSchemas';
import { errorMessages } from '~/constants/errorMessages.ts';
import { AuthorityTermType, TAssetManager, TManagersAndRepresentatives } from './types';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { getValidationFormValue } from '~/helpers/utils';

export const emailValidationSchema = emailSchema.test(
  'unique-all-emails',
  function (emailAddress, context) {
    if (!emailAddress) return true;
    const emails: string[] = context.from?.[1]?.value.assetManagers.flatMap(
      (managers: TAssetManager) => managers.emailAddress || []
    );
    if (emails.filter((address) => address === emailAddress).length > 1) {
      return this.createError({
        path: this.path,
        message: errorMessages.UNIQUE_EMAIL,
      });
    }
    return true;
  }
);

const identNumberValidationSchema = (field: string, schema: Yup.Schema) =>
  schema.test('unique-all-RNOKPP', function (identNumber, context) {
    if (!identNumber) return true;
    const identNumbers: string[] = context.from?.[1]?.value.assetManagers.flatMap(
      (managers: TAssetManager) => managers[field as keyof typeof managers] || []
    );
    if (
      identNumbers.filter((identificationNumber) => identificationNumber === identNumber).length > 1
    ) {
      return this.createError({
        path: this.path,
        message: errorMessages.UNIQUE_IDENTIFICATION_NUMBER,
      });
    }
    return true;
  });

export const signatureAuthorityDetailsSchema = (isAccountHolder: boolean) =>
  requiredOptionSchema.test('contains-first-sign', function (signatureAuthorityDetails, context) {
    if (!signatureAuthorityDetails || isAccountHolder) return true;
    const signatures = context.from?.[2]?.value.assetManagers?.map(
      (managers: TAssetManager) => managers.signatureAuthorityDetails || []
    );

    if (!signatures.some((item: TAutocompleteOption) => item.value === '1')) {
      return this.createError({
        path: this.path,
        message: errorMessages.FIRST_SIGN_MISSING,
      });
    }

    return true;
  });

export const validationSchema = ({
  isAccountHolder,
  isReidentification,
}: {
  isAccountHolder: boolean;
  isReidentification: boolean;
}) =>
  Yup.object().shape({
    assetManagers: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().nullable(),
          managerId: Yup.string().nullable(),
          lastName: nameSchema.required(errorMessages.FIELD_REQUIRED),
          firstName: nameSchema.required(errorMessages.FIELD_REQUIRED),
          middleName: nameSchema,
          identificationNumber: identNumberValidationSchema(
            'identificationNumber',
            RNOKPPIdentNumberSchema('foreignTaxNumber')
          ),
          foreignTaxNumber: identNumberValidationSchema(
            'foreignTaxNumber',
            foreignTaxNumberSchema('identificationNumber')
          ),
          contactNumbers: Yup.array().of(phoneSchema),
          emailAddress: Yup.array().of(emailValidationSchema),
          position: requiredOptionSchema,
          signatureAuthorityDetails: signatureAuthorityDetailsSchema(isAccountHolder),
          authorityTermType: isReidentification
            ? Yup.string().nullable()
            : Yup.string().nullable().required(errorMessages.FIELD_REQUIRED),
          authorityExpirationDate: Yup.string().when(
            'authorityTermType',
            function ([authorityTermType], schema) {
              if (authorityTermType === AuthorityTermType.UNTIL_DATE)
                return schema.required(errorMessages.FIELD_REQUIRED);
              else return schema.nullable();
            }
          ),
          accessAfterAuthorityExpiration: Yup.string().when(
            'authorityTermType',
            function ([authorityTermType], schema) {
              if (authorityTermType === AuthorityTermType.UNTIL_DATE)
                return schema.required(errorMessages.FIELD_REQUIRED);
              else return schema.nullable();
            }
          ),
        })
      )
      .nullable(),
    isUnifiedAccountAccess: Yup.boolean().nullable(),
    isClientRepresentativesAbsent: Yup.boolean().nullable(),
    clientRepresentatives: Yup.array()
      .of(
        Yup.object().shape({
          lastName: Yup.string()
            .nullable()
            .when('isClientRepresentativesAbsent', function ([], schema, options) {
              return getValidationFormValue<TManagersAndRepresentatives>(options, 1)
                ?.isClientRepresentativesAbsent
                ? schema.nullable().notRequired()
                : nameSchema.required(errorMessages.FIELD_REQUIRED);
            }),
          firstName: Yup.string()
            .nullable()
            .when('isClientRepresentativesAbsent', function ([], schema, options) {
              return getValidationFormValue<TManagersAndRepresentatives>(options, 1)
                ?.isClientRepresentativesAbsent
                ? schema.nullable().notRequired()
                : nameSchema.required(errorMessages.FIELD_REQUIRED);
            }),
          middleName: Yup.string()
            .nullable()
            .when('isClientRepresentativesAbsent', function ([], schema, options) {
              return getValidationFormValue<TManagersAndRepresentatives>(options, 1)
                ?.isClientRepresentativesAbsent
                ? schema.nullable().notRequired()
                : nameSchema;
            }),
          identificationNumber: Yup.string()
            .nullable()
            .when('isClientRepresentativesAbsent', function ([], _, options) {
              return RNOKPPIdentNumberSchema(
                'foreignTaxNumber',
                getValidationFormValue<TManagersAndRepresentatives>(options, 1)
                  ?.isClientRepresentativesAbsent
              );
            }),
          foreignTaxNumber: Yup.string()
            .nullable()
            .when('isClientRepresentativesAbsent', function ([], _, options) {
              return foreignTaxNumberSchema(
                'identificationNumber',
                getValidationFormValue<TManagersAndRepresentatives>(options, 1)
                  ?.isClientRepresentativesAbsent
              );
            }),
          citizenshipCountry: Yup.object().when(
            'isClientRepresentativesAbsent',
            function ([], _, options) {
              return getValidationFormValue<TManagersAndRepresentatives>(options, 1)
                ?.isClientRepresentativesAbsent
                ? notRequiredOptionSchema
                : requiredOptionSchema;
            }
          ),
          residenceCountry: Yup.object().when(
            'isClientRepresentativesAbsent',
            function ([], _, options) {
              return getValidationFormValue<TManagersAndRepresentatives>(options, 1)
                ?.isClientRepresentativesAbsent
                ? notRequiredOptionSchema
                : requiredOptionSchema;
            }
          ),
          address: Yup.object().shape({
            registrationAddress: Yup.object()
              .when('isClientRepresentativesAbsent', function ([], _, options) {
                return getValidationFormValue<TManagersAndRepresentatives>(options, 2)
                  ?.isClientRepresentativesAbsent
                  ? addressNotRequiredSchema
                  : addressSchema;
              })
              .nullable(),
            permanentAddress: Yup.object()
              .when('isClientRepresentativesAbsent', function ([], _, options) {
                return getValidationFormValue<TManagersAndRepresentatives>(options, 2)
                  ?.isClientRepresentativesAbsent
                  ? addressNotRequiredSchema
                  : addressSchema;
              })
              .nullable(),
            coincidences: Yup.boolean().nullable(),
          }),
        })
      )
      .nullable(),
  });
